import { zodResolver } from '@hookform/resolvers/zod'
import type { HookAPI } from 'antd/es/modal/useModal'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'
import { stockImportsApi } from '@/pages/Exchanges/tabs/StockImports/api/stockImportsApi'
import type {
  ProductSupplierDto,
  StoreModel,
} from '@/pages/Exchanges/tabs/StockImports/types/stockImports.types'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import StoreCreationFields from './StoreCreationFields'
import {
  buildStoreFormDefaults,
  buildStoreSupplierOptions,
  STORE_PRICE_SOURCE_LABELS,
  type StoreCreationFormType,
  storeCreationSchema,
  toStoreRequest,
} from './StoreCreationModal.config'
import {
  isStoreDeactivation,
  resolveStoreStatusId,
  STORE_DEACTIVATION_CONFIRM_TEXT,
} from './storeFormHelpers'

interface Props {
  open: boolean
  supplier: ProductSupplierDto | null
  suppliers?: ProductSupplierDto[]
  showSupplierSelect?: boolean
  store: StoreModel | null
  stores: StoreModel[]
  modal: HookAPI
  onClose: () => void
  onSaved: (store: StoreModel) => void
}

function StoreCreationModal({
  open,
  supplier,
  suppliers = [],
  showSupplierSelect = false,
  store,
  stores,
  modal,
  onClose,
  onSaved,
}: Props) {
  const [saving, setSaving] = useState(false)
  const submittingRef = useRef(false)
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<StoreCreationFormType>({
    resolver: zodResolver(storeCreationSchema),
    defaultValues: buildStoreFormDefaults(store, supplier),
    mode: 'onTouched',
    shouldFocusError: false,
  })

  const saveStore = async (values: StoreCreationFormType) => {
    if (isStoreDeactivation(store, values)) {
      const confirmed = await modal.confirm({
        title: 'Выключить склад?',
        content: STORE_DEACTIVATION_CONFIRM_TEXT,
        okText: 'Выключить',
        cancelText: 'Отмена',
      })
      if (!confirmed) return
    }

    const storeStatusId = await resolveStoreStatusId(store, stores)
    if (!storeStatusId) {
      toast.error('Не найден UUID статуса склада')
      return
    }

    const payload = toStoreRequest(values, storeStatusId)
    setSaving(true)
    const toastId = toast.loading(
      store ? 'Сохранение склада...' : 'Создание склада...'
    )
    try {
      const saved = store
        ? await stockImportsApi.updateStore(store.id, {
            ...payload,
            owner: store.owner,
          })
        : await stockImportsApi.createStore(payload)
      toast.success(store ? 'Склад сохранён' : 'Склад создан', { id: toastId })
      onSaved(saved)
    } catch (error) {
      toast.dismiss(toastId)
      throw error
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async (values: StoreCreationFormType) => {
    if (submittingRef.current) return
    submittingRef.current = true
    try {
      await saveStore(values)
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      submittingRef.current = false
    }
  }

  return (
    <AtomModalWithForm
      title={store ? 'Редактирование склада' : 'Новый склад'}
      submitText="Сохранить"
      open={open}
      submitting={saving}
      submitDisabled={Boolean(store) && !isDirty}
      onCancel={onClose}
      onSubmit={(event) => {
        event.preventDefault()
        void handleSubmit(handleSave, () => handleFormSubmitError(false))()
      }}
    >
      {store && (
        <AtomNotice description="Меняется настройкой загрузки остатков">
          Цены склада: {STORE_PRICE_SOURCE_LABELS[store.priceSource]}
        </AtomNotice>
      )}
      <StoreCreationFields
        control={control}
        register={register}
        errors={errors}
        supplierOptions={buildStoreSupplierOptions(suppliers, supplier, store)}
        showSupplierSelect={showSupplierSelect}
        isOrganisationSelected={isOrganisationSelected}
      />
    </AtomModalWithForm>
  )
}

export default StoreCreationModal
